import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import { Lock, MessageCircle, Send, CheckCircle, Loader2, User, Phone, Mail, Store } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// مكون نموذج طلب التفعيل
export const SubscriptionWarning = () => {
  const { isActive, canEdit, user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [error, setError] = useState(null);
  
  // بيانات النموذج
  const [formData, setFormData] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    store_name: user?.store_name || "",
  });
  
  // لا تظهر شيء إذا الحساب مفعّل
  if (isActive && canEdit) return null;
  
  // ✨ لا تظهر للموظفين (sub-accounts) — هم يرثون ترخيص المالك تلقائياً
  if (user?.is_employee) return null;
  
  // دالة إرسال طلب التفعيل
  const sendActivationRequest = async (e) => {
    e.preventDefault();
    
    // التحقق من الحقول المطلوبة
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim()) {
      setError("يرجى ملء جميع الحقول المطلوبة");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/owner/request-activation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setRequestSent(true);
      } else {
        setError(data.message || "حدث خطأ");
      }
    } catch (err) {
      setError("فشل إرسال الطلب. حاول مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };
  
  // إذا الحساب غير مفعّل
  if (!canEdit) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <Card className="bg-slate-800 border-amber-500 max-w-lg w-full animate-fade-in-up my-8">
          <CardContent className="p-6 space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center">
                <Lock className="w-8 h-8 text-amber-500" />
              </div>
              
              <h2 className="text-xl font-bold text-white mt-4">
                طلب تفعيل التطبيق
              </h2>
              
              <p className="text-slate-400 text-sm mt-2">
                أدخل بياناتك وسيتم التواصل معك
              </p>
            </div>
            
            {/* السعر */}
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-emerald-400 font-medium mb-1">سعر التطبيق:</p>
              <p className="text-3xl font-bold text-white">
                690,000
                <span className="text-lg text-slate-400 mr-2">د.ع</span>
              </p>
              <p className="text-sm text-slate-500 mt-1">دفعة واحدة فقط</p>
            </div>

            {/* طريقة الدفع - عبر الدعم الفني */}
            <div className="bg-slate-900/50 rounded-lg p-3">
              <p className="text-blue-400 font-medium mb-2 text-center text-sm">طريقة الدفع:</p>
              <div className="flex justify-center">
                <div className="flex flex-col items-center bg-emerald-900/30 px-6 py-3 rounded-lg border border-emerald-500/30">
                  <MessageCircle className="w-6 h-6 text-emerald-400 mb-1" />
                  <span className="text-white font-bold text-lg">تواصل مع الدعم الفني</span>
                  <span className="text-emerald-300 text-sm">للحصول على تفاصيل الدفع</span>
                </div>
              </div>
            </div>

            {/* نموذج الطلب أو رسالة النجاح */}
            {requestSent ? (
              <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-lg p-4 text-center">
                <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                <p className="text-emerald-400 font-bold text-lg">تم إرسال طلب التفعيل!</p>
                <p className="text-slate-400 text-sm mt-2">
                  سيتم التواصل معك قريباً بعد استلام الدفعة
                </p>
              </div>
            ) : (
              <form onSubmit={sendActivationRequest} className="space-y-3">
                {/* الاسم */}
                <div>
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <User className="w-4 h-4" />
                    الاسم الكامل *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                    placeholder="أدخل اسمك الكامل"
                    required
                  />
                </div>
                
                {/* الرقم */}
                <div>
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    رقم الهاتف *
                  </Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                    placeholder="07xxxxxxxxx"
                    dir="ltr"
                    required
                  />
                </div>
                
                {/* الإيميل */}
                <div>
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    البريد الإلكتروني *
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                    placeholder="example@email.com"
                    dir="ltr"
                    required
                  />
                </div>
                
                {/* اسم المتجر (اختياري) */}
                <div>
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <Store className="w-4 h-4" />
                    اسم المتجر (اختياري)
                  </Label>
                  <Input
                    value={formData.store_name}
                    onChange={(e) => setFormData({...formData, store_name: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                    placeholder="اسم متجرك أو نشاطك التجاري"
                  />
                </div>

                {/* زر الإرسال */}
                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white py-4 mt-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 me-2 animate-spin" />
                      جاري الإرسال...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 me-2" />
                      أرسل طلب التفعيل
                    </>
                  )}
                </Button>
              </form>
            )}
            
            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
            
            <p className="text-slate-500 text-xs text-center">
              بعد إرسال الطلب والدفع، سيتم تفعيل حسابك
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return null;
};

// مكون القفل
export const LockedOverlay = ({ children, message }) => {
  const { canEdit } = useAuth();
  
  if (canEdit) return children;
  
  return (
    <div className="relative">
      <div className="pointer-events-none opacity-50 blur-[1px]">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
        <div className="bg-slate-800 px-4 py-2 rounded-lg flex items-center gap-2">
          <Lock className="w-4 h-4 text-amber-500" />
          <span className="text-amber-200 text-sm">
            {message || "حسابك بانتظار التفعيل"}
          </span>
        </div>
      </div>
    </div>
  );
};

// Hook للتحقق من إمكانية التحرير
export const useCanEdit = () => {
  const { canEdit } = useAuth();
  return canEdit;
};
